import { clubCapabilities, selectedClubId } from '../state/club-session';
import { assertMaintenanceAvailable, inspectMaintenanceResponse, maintenanceActive } from './maintenance-state';
/**
 * API Simulator Service
 * Realiza la llamada HTTP real al backend en todos los endpoints.
 * Si el endpoint aún no existe en el backend (404, 501, fallo de red o error),
 * recupera y devuelve automáticamente el mock (.json) correspondiente
 * de la carpeta de mocks del servicio (/mocks/{service}/{mockName}.json).
 *
 * En caso de recibir un 401 Unauthorized en llamadas protegidas:
 * 1. Invoca a POST /api/auth/refresh enviando cookies de sesión.
 * 2. Si el refresco tiene éxito, reintenta la petición original.
 * 3. Si el refresco falla, ejecuta logout inmediato y redirige al usuario a /login.
 */
import { recordApiCall, getApiUrl } from './http-client';
import { getMockJson } from './mock-registry';
import { environment } from '../../../environments/environment';

export interface SimulatorRequestOptions<T> {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  body?: any;
  service: string; // ej. 'clubs', 'market', 'competition', 'stadium-economy', 'auth', 'squad', 'leagues'
  mockName?: string; // nombre del fichero .json sin extensión
  transformMock?: (mockData: any) => Promise<T> | T;
  simulatedDelayMs?: number;
  allowMockFallback?: boolean;
  _isRetry?: boolean;
  headers?: Record<string, string>;
}

export type SessionExpiredHandler = () => void | Promise<void>;
let sessionExpiredHandler: SessionExpiredHandler | null = null;
let isHandlingSessionExpiry = false;

export function registerSessionExpiredHandler(handler: SessionExpiredHandler): void {
  sessionExpiredHandler = handler;
}

export function triggerSessionExpired(): void {
  if (maintenanceActive()) return;
  if (isHandlingSessionExpiry) return;
  isHandlingSessionExpiry = true;
  setTimeout(() => {
    isHandlingSessionExpiry = false;
  }, 4000);

  if (sessionExpiredHandler) {
    try {
      const res = sessionExpiredHandler();
      if (res instanceof Promise) {
        res.catch((e) => console.error('[Auth] Error en sessionExpiredHandler:', e));
      }
    } catch (e) {
      console.error('[Auth] Error ejecutando sessionExpiredHandler:', e);
    }
  }
}

function getStoredAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return (
    localStorage.getItem('fm_real_api_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('access_token')
  );
}

let refreshPromise: Promise<boolean> | null = null;

/**
 * Llama a POST /api/auth/refresh enviando las cookies HttpOnly (credentials: 'include').
 * Reutiliza una única promesa en vuelo para evitar llamadas duplicadas simultáneas.
 */
export async function refreshAuthToken(): Promise<boolean> {
  assertMaintenanceAvailable();
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const url = getApiUrl('/api/auth/refresh');
      const reqHeaders: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      const token = getStoredAuthToken();
      if (token && token !== 'cookie-session' && token.length > 5) {
        reqHeaders['Authorization'] = `Bearer ${token}`;
      }

      console.info('[Auth] Solicitando refresco de sesión en POST /api/auth/refresh...');
      const res = await fetch(url, {
        method: 'POST',
        headers: reqHeaders,
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({}),
      });
      await inspectMaintenanceResponse(res);

      if (res.ok) {
        let responseData: any = null;
        try {
          const text = await res.text();
          if (text && text.trim().length > 0) {
            responseData = JSON.parse(text);
          }
        } catch {
          // Vacío o 204
        }

        const newToken =
          responseData?.token ||
          responseData?.accessToken ||
          responseData?.access_token ||
          responseData?.jwt;

        if (newToken && typeof localStorage !== 'undefined') {
          localStorage.setItem('fm_real_api_token', newToken);
          localStorage.setItem('token', newToken);
        }

        console.info('[Auth] Sesión refrescada con éxito vía /api/auth/refresh');
        return true;
      } else {
        console.warn(`[Auth] /api/auth/refresh respondió con HTTP ${res.status}. Refresco no válido.`);
        return false;
      }
    } catch (err) {
      if (maintenanceActive()) throw err;
      console.warn('[Auth] Error de red al invocar /api/auth/refresh:', err);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export const apiSimulator = {
  /**
   * Ejecuta la petición real contra el backend.
   * Si el backend responde con 401, intenta refrescar con /api/auth/refresh y reintentar.
   * Si el refresco falla, hace logout y redirige.
   * Si el endpoint no existe (404, 501 o error de red), carga el mock correspondiente.
   */
  async callWithMockFallback<T = any>(options: SimulatorRequestOptions<T>): Promise<T> {
    assertMaintenanceAvailable();
    const selected = selectedClubId();
    const owner = /^\/api\/clubs\/([^/?]+)/.exec(options.endpoint)?.[1];
    if (selected && owner && decodeURIComponent(owner) !== selected && options.method !== 'GET')
      throw Object.assign(new Error('El club de la operación no coincide con el seleccionado.'),{status:409,code:'CLUB_CONTEXT_CHANGED'});
    if (selected && owner && decodeURIComponent(owner) === selected && options.method !== 'GET') {
      const action = (options.endpoint.includes('/lineup') || options.endpoint.includes('/substitution-windows')) ? 'saveLineup'
        : options.endpoint.includes('/loans/') ? 'repayLoan'
        : options.endpoint.endsWith('/loans') ? 'borrow'
        : options.endpoint.includes('/coaches/') ? 'bidCoach'
        : options.endpoint.includes('/sponsors/') ? 'signSponsor'
        : options.endpoint.includes('/stadium/name') ? 'renameStadium'
        : options.endpoint.includes('/tickets') ? 'changeTicketPrice'
        : options.endpoint.includes('/upgrades') ? 'upgradeClub'
        : options.endpoint.includes('/market/club-listings') && !options.endpoint.includes('/offers') ? 'sellPlayer' : 'placeOffer';
      const gate=clubCapabilities()?.actions[action];
      if (!gate?.available) throw Object.assign(new Error('Esta acción no está disponible para el club seleccionado.'),{status:409,code:gate?.reason || 'CAPABILITY_UNAVAILABLE'});
    }
    if (options.allowMockFallback !== true && (options.endpoint.startsWith('/api/clubs/') || options.endpoint.startsWith('/api/private-leagues')))
      options = { ...options, allowMockFallback: false };
    const {
      method,
      endpoint,
      body,
      service,
      mockName,
      transformMock,
      simulatedDelayMs = 80,
      _isRetry = false,
    } = options;

    return recordApiCall(
      method,
      endpoint,
      body || null,
      async () => {
        const url = getApiUrl(endpoint);
        let networkSuccess = false;
        let responseData: any = null;
        let httpStatus = 200;

        try {
          const reqHeaders: Record<string, string> = {
            Accept: 'application/json',
            ...(body ? { 'Content-Type': 'application/json' } : {}),
            ...(options.headers || {}),
          };

          const token = getStoredAuthToken();
          if (token && token !== 'cookie-session' && token.length > 5) {
            reqHeaders['Authorization'] = `Bearer ${token}`;
          }

          const fetchOptions: RequestInit = {
            method,
            headers: reqHeaders,
            credentials: 'include',
            cache: 'no-store',
            ...(body ? { body: JSON.stringify(body) } : {}),
          };

          assertMaintenanceAvailable();
          const res = await fetch(url, fetchOptions);
          await inspectMaintenanceResponse(res);
          httpStatus = res.status;

          // Si el endpoint responde con 2xx (incluido 204 No Content), tomamos la respuesta real del backend
          if (res.ok) {
            if (res.status === 204) {
              responseData = { success: true };
            } else {
              try {
                const text = await res.text();
                responseData = text && text.trim().length > 0 ? JSON.parse(text) : { success: true };
              } catch (parseError) {
                if (options.allowMockFallback === false) throw parseError;
                responseData = { success: true };
              }
            }
            networkSuccess = true;
            if (Array.isArray(responseData)) {
              (responseData as any)._httpStatus = httpStatus;
              (responseData as any)._isMock = false;
              return responseData;
            }
            return {
              ...responseData,
              _httpStatus: httpStatus,
              _isMock: false,
            };
          } else if (res.status === 401) {
            // Manejo especial de 401 Unauthorized
            if (endpoint.includes('/api/auth/login')) {
              let errorMsg = 'Credenciales no válidas.';
              try {
                const errData = await res.json();
                errorMsg =
                  errData.detail ||
                  errData.message ||
                  errData.title ||
                  errData.error ||
                  errorMsg;
              } catch {}
              const err = new Error(errorMsg);
              (err as any).status = 401;
              throw err;
            }

            if (endpoint.includes('/api/auth/refresh')) {
              const err = new Error('No se pudo refrescar la sesión.');
              (err as any).status = 401;
              throw err;
            }

            if (endpoint.includes('/api/auth/logout')) {
              console.info('[Auth] /api/auth/logout respondió status 401. Cierre de sesión finalizado.');
              return { success: true, _httpStatus: 401, _isMock: false } as any;
            }

            // Para cualquier otro endpoint que retorne 401:
            if (!_isRetry) {
              console.warn(
                `[API] 401 en ${method} ${endpoint}. Solicitando refresco a /api/auth/refresh...`
              );
              const refreshed = await refreshAuthToken();

              if (refreshed) {
                console.info(
                  `[API] Token refrescado con éxito. Reintentando ${method} ${endpoint}...`
                );
                return await apiSimulator.callWithMockFallback<T>({
                  ...options,
                  _isRetry: true,
                });
              } else {
                console.warn(
                  `[API] Refresco en /api/auth/refresh falló tras 401 en ${method} ${endpoint}. Ejecutando logout.`
                );
                triggerSessionExpired();
                const err = new Error('Sesión expirada. Por favor, inicia sesión de nuevo.');
                (err as any).status = 401;
                throw err;
              }
            } else {
              console.warn(
                `[API] Reintento de ${method} ${endpoint} devolvió 401 de nuevo. Ejecutando logout.`
              );
              triggerSessionExpired();
              const err = new Error('Sesión expirada. Por favor, inicia sesión de nuevo.');
              (err as any).status = 401;
              throw err;
            }
          } else if (res.status === 409) {
            // Manejo específico de HTTP 409 (Conflict), ej. saldo insuficiente al ofertar
            let errorBody: any = null;
            try {
              const text = await res.text();
              errorBody = text && text.trim().length > 0 ? JSON.parse(text) : null;
            } catch {}

            const failure = errorBody?.failure;
            const errorCode = errorBody?.code || failure?.code;
            const requestId = errorBody?.requestId;
            const errorMsg =
              failure?.message ||
              errorBody?.message ||
              errorBody?.detail ||
              'Conflicto al procesar la solicitud (HTTP 409).';

            const err: any = new Error(errorMsg);
            err.status = 409;
            err.statusText = res.statusText;
            err.code = errorCode;
            err.requestId = requestId;
            err.responseBody = errorBody;
            err.failure = failure;
            throw err;
          } else {
            // Manejo de errores HTTP 4xx y 5xx desde el backend
            let errorMsg = res.statusText
              ? `Error HTTP ${res.status}: ${res.statusText}`
              : `Error HTTP ${res.status}`;
            let errorBody: any = null;
            try {
              const text = await res.text();
              if (text && text.trim().length > 0) {
                errorBody = JSON.parse(text);
                errorMsg =
                  errorBody?.detail ||
                  errorBody?.message ||
                  errorBody?.title ||
                  errorBody?.error ||
                  errorMsg;
              }
            } catch {}

            const err: any = new Error(errorMsg);
            err.status = res.status;
            err.statusText = res.statusText;
            err.code = errorBody?.code;
            err.requestId = errorBody?.requestId;
            err.responseBody = errorBody;

            const retryAfterHeader = res.headers.get('Retry-After');
            if (retryAfterHeader) {
              const parsed = parseInt(retryAfterHeader, 10);
              err.retryAfter = isNaN(parsed) ? 60 : parsed;
            } else if (res.status === 429) {
              err.retryAfter = 60;
            }

            // En endpoints de autorización, autenticación, /api/me/competitions, remodelaciones o tickets de estadio
            // o si los mocks están deshabilitados: propagar inmediatamente el error real y nunca caer en mock silencioso
            if (
              options.allowMockFallback === false ||
              !environment.enableMockFallback ||
              endpoint.includes('/api/auth/') ||
              endpoint.includes('/api/me/competitions') ||
              endpoint.includes('/stadium') ||
              endpoint.includes('/sponsors')
            ) {
              throw err;
            }

            console.warn(
              `[API Simulator] Endpoint ${method} ${endpoint} respondió con HTTP ${res.status}. Usando mock desde mocks/${service}/${mockName}.json`
            );
          }
        } catch (fetchErr: any) {
          if (environment.pwaEnabled && fetchErr instanceof TypeError) {
            window.dispatchEvent(new Event('lom:connection-lost'));
          }
          // Si es un error del servidor (status code presente) o endpoints sensibles: propagar siempre
          if (
            fetchErr?.status ||
            fetchErr?.failure ||
            fetchErr?.message?.includes('Sesión expirada') ||
            fetchErr?.message?.includes('Credenciales no válidas') ||
            endpoint.includes('/api/auth/') ||
            endpoint.includes('/api/me/competitions') ||
            endpoint.includes('/stadium') ||
            endpoint.includes('/sponsors') ||
            options.allowMockFallback === false ||
            !environment.enableMockFallback
          ) {
            throw fetchErr;
          }

          console.warn(
            `[API Simulator] Endpoint ${method} ${endpoint} no alcanzable (${fetchErr?.message || 'Error de red'}). Usando mock desde mocks/${service}/${mockName}.json`
          );
        }

        // Si no se obtuvo respuesta 2xx del backend real y no fue un fallo de 401/auth, cargamos el mock
        if (!networkSuccess) {
          if (simulatedDelayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, simulatedDelayMs));
          }

          const rawMock = mockName ? await getMockJson(service, mockName) : {};
          const finalData = transformMock ? await transformMock(rawMock) : rawMock;

          if (Array.isArray(finalData)) {
            (finalData as any)._httpStatus = 200;
            (finalData as any)._isMock = true;
            (finalData as any)._mockSource = `mocks/${service}/${mockName}.json`;
            return finalData;
          }

          return {
            ...finalData,
            _httpStatus: 200,
            _isMock: true,
            _mockSource: `mocks/${service}/${mockName}.json`,
          };
        }

        return responseData;
      },
      0
    );
  },
};
